import {TextControl} from '@wordpress/components';
import {__} from '@wordpress/i18n';


import styles from './font-size.pcss';


type Unit = 'px' | 'em' | 'rem';

type SelectUnit = {
	value: string;
	label: string;
	step?: number;
};


export type Props = {
	value: string;
	onChange: ( value: string ) => void;
	label: string;
}

export const UNITS: { [key in Unit]: SelectUnit } = {
	px: {
		value: 'px',
		label: 'px',
		step: 1,
	},
	em: {
		value: 'em',
		label: 'em',
		step: 0.01,
	},
	rem: {
		value: 'rem',
		label: 'rem',
		step: 0.01,
	},
};

/**
 * Split a value into a number and a unit.
 *
 * Default unit is 'px'.
 *
 * @see \Advanced_Sidebar_Menu\Widget_Options\Shared\Styles::translate_font_size
 * @see parseQuantityAndUnitFromRawValue in @wordpress/components/src/unit-control/utils.ts
 */
export function splitValue( value: string | number ): { number: number | '', unit: Unit } {
	const raw = value.toString().trim();
	const number: number | typeof NaN = parseFloat( raw );
	const unitMatch = raw.match( /[\d.\-+]*\s*(.*)/ );
	let matchedUnit = unitMatch?.[ 1 ]?.toLowerCase();

	if ( undefined === matchedUnit || ! [ 'px', 'rem', 'em' ].includes( matchedUnit ) ) {
		matchedUnit = 'px';
	}

	return {
		number: Number.isFinite( number ) ? number : '',
		unit: matchedUnit as Unit,
	};
}


/**
 * Font size with a unit selector.
 *
 * Inspired by the experimental @wordpress/components UnitControl.
 * - May be able to switch to that one day if it becomes stable.
 *
 * @since 9.7.0
 */
const FontSize = ( {value, onChange, label}: Props ) => {
	const split = splitValue( value );

	return (
		<div className={styles.wrap}>
			<TextControl
				className={styles.number}
				label={label}
				step={UNITS[ split.unit ].step ?? 0.01}
				type={'number'}
				value={split.number ?? ''}
				onChange={size => {
					onChange( `${size}${split.unit}` );
				}}
				// @ts-expect-error -- Not technically supported until WP 6.7
				__nextHasNoMarginBottom
			/>
			<select
				className={styles.unit + ' components-select-control__input'}
				aria-label={__( 'Select unit', 'advanced-sidebar-menu' )}
				value={split.unit}
				onChange={ev => {
					onChange( `${split.number}${ev.target.value}` );
				}}
			>
				{Object.values( UNITS ).map( unit => (
					<option value={unit.value} key={unit.value}>
						{unit.label}
					</option>
				) )}
			</select>
		</div>
	);
};

export default FontSize;
